import { z } from 'zod';
import { uuid, isoTimestamp, storeCode, cents } from './common.js';
/**
 * The list (API §5). One list, grouped by supermarket — not four endpoints.
 */
/** Client supplies the id, so an offline add already has its final identity. */
export const addListItemRequest = z.object({
    id: uuid,
    generic_product_id: uuid,
    variant_id: uuid.nullish(),
    /** Omitted or null = "Any" (ARCHITECTURE §6.2). */
    store: storeCode.nullish(),
    quantity: z.number().int().positive().default(1),
    note: z.string().max(500).nullish(),
    suggestion_reason: z.string().max(300).nullish(),
});
/**
 * Moving an item between stores is a PATCH to `store`, not a delete and re-add
 * — that is what makes "bought elsewhere" teach history correctly (§6.6).
 */
export const patchListItemRequest = z
    .object({
    quantity: z.number().int().positive().optional(),
    note: z.string().max(500).nullish(),
    store: storeCode.nullish(),
    variant_id: uuid.nullish(),
    updated_at: isoTimestamp.optional(),
})
    .refine((v) => Object.keys(v).some((k) => k !== 'updated_at'), {
    message: 'no fields to update',
});
/** `store` is required only for Any items — it resolves where it was bought. */
export const checkoffRequest = z.object({
    store: storeCode.nullish(),
    purchased_at: isoTimestamp.optional(),
    /** Optional client-supplied id so a retried check-off is trivially idempotent. */
    purchase_id: uuid.optional(),
});
export const listItemDto = z.object({
    id: uuid,
    generic_product: z.object({ id: uuid, name: z.string(), category: z.string().nullable() }),
    variant: z
        .object({ id: uuid, name: z.string(), store: storeCode, size_label: z.string().nullable() })
        .nullable(),
    quantity: z.number().int(),
    note: z.string().nullable(),
    price_cents: cents.nullable(),
    /** ADAPTERS §5.2 — never present an old price as current. */
    stale: z.boolean(),
    price_fetched_at: isoTimestamp.nullable(),
    suggestion_reason: z.string().nullable(),
    updated_at: isoTimestamp,
});
export const listGroupDto = z.object({
    /** null for the Any group, which always sorts first. */
    store: storeCode.nullable(),
    label: z.string(),
    /** A store whose adapter is down still returns its group, flagged (§6.1). */
    adapter_ok: z.boolean(),
    items: z.array(listItemDto),
});
export const suggestionDto = z.object({
    generic_product: z.object({ id: uuid, name: z.string() }),
    /** Every suggestion shows why (ARCHITECTURE §3.2). Not optional. */
    reason: z.string(),
    confidence: z.number().min(0).max(1),
    suggested_variant: z.object({ id: uuid, name: z.string() }).nullable(),
});
export const listResponse = z.object({
    groups: z.array(listGroupDto),
    /** Returned inline, never as a separate fetch (API §5.3). */
    suggestions: z.array(suggestionDto),
});
/**
 * REVIEW G2. Every path to a generic product went through variant
 * classification, so the Ekoplaza speltbrood in the user story could not be
 * created at all. With three of four stores on manual entry after the
 * ADAPTERS §7 spike, this endpoint is the main way products get created.
 */
export const createGenericProductRequest = z.object({
    id: uuid,
    name: z.string().min(1).max(200),
    category: z.string().max(100).nullish(),
});
/**
 * Add several named items to the list in one call — a paste-a-list / send-a-
 * recipe-to-the-list bulk add. Each item is resolved-or-created as a generic
 * product by slug (so it does not fragment the household's history), then added
 * as an "Any" list item with the measure in the note.
 *
 * This is a **groceries-owned** endpoint. The recipes module calls it over HTTP
 * with shared ids — the sanctioned cross-module seam ("cross-talk through shared
 * IDs/URLs, not imports") — so groceries never learns that recipes exist.
 */
export const bulkAddListItem = z.object({
    name: z.string().min(1).max(200),
    quantity: z.number().int().positive().default(1),
    /** Free-text, e.g. the recipe's measure and title: "400 g — Risotto". */
    note: z.string().max(500).nullish(),
    /** Optional aisle category; when given it is written as a manual choice. */
    category: z.string().max(100).nullish(),
});
export const bulkAddListItemsRequest = z.object({
    items: z.array(bulkAddListItem).min(1).max(100),
});
export const bulkAddListItemsResponse = z.object({
    added: z.array(z.object({ name: z.string(), list_item_id: uuid, generic_product_id: uuid })),
    /** An item that could not be added (a validation failure); the rest still land. */
    rejected: z.array(z.object({ name: z.string(), code: z.string(), message: z.string() })),
});
/**
 * Aisle order (API §5.1). PUT replaces the whole order — a drag-and-drop screen
 * has the full list in hand, and sending it whole avoids reconciling moves.
 */
export const putCategoryOrderRequest = z.object({
    categories: z.array(z.string().min(1).max(100)).max(200),
});
export const categoryOrderResponse = z.object({
    store: storeCode,
    categories: z.array(z.object({
        category: z.string(),
        position: z.number().int().nullable(),
        /** Unpositioned categories are flagged so the client shows them at the bottom. */
        positioned: z.boolean(),
    })),
});
//# sourceMappingURL=list.js.map